/**
 * @file socket.service.ts
 * @version 1.9.0
 * @datetime 2026-09-06T01:15:00+03:00
 * @description Socket.IO client service copied directly from ustatmuhasebe to communicate with the existing server without modifying any server code.
 */

import { Injectable, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SocketService {
  private socket: Socket | null = null;
  private connected = signal(false);
  readonly isConnected = this.connected.asReadonly();

  connect(url: string = environment.apiBaseUrl) {
    if (this.socket?.connected) {
      return;
    }
    this.socket = io(url, {
      transports: ['websocket', 'polling'],
      timeout: 8000,
      reconnectionAttempts: 3,
      reconnectionDelay: 5000,
    });
    this.socket.on('connect', () => {
      this.connected.set(true);
    });
    this.socket.on('disconnect', () => {
      this.connected.set(false);
    });
    this.socket.on('connect_error', (err) => {
      console.warn(`[WARN][SocketService] - Socket.io connection attempt notice:`, err.message);
    });
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
    this.connected.set(false);
  }

  emit(event: string, data?: any) {
    this.socket?.emit(event, data);
  }

  on(event: string, callback: (...args: any[]) => void) {
    this.socket?.on(event, (...args) => {
      callback(...args);
    });
  }

  off(event: string, callback?: (...args: any[]) => void) {
    this.socket?.off(event, callback);
  }

  /**
   * Requests temporary AI API key on-demand from ustat-server.js via Socket.IO get_key event.
   */
  async requestApiKey(metadata?: { company_id?: string; user_id?: string; phone?: string; action?: string }): Promise<string | null> {
    this.connect(environment.apiBaseUrl);

    return new Promise((resolve) => {
      let resolved = false;

      const handler = (data: any) => {
        if (!resolved) {
          resolved = true;
          this.off('key_response', handler);
          if (typeof data === 'string' && data.length > 5) {
            resolve(data);
          } else if (data && typeof data === 'object' && data.apiKey) {
            resolve(data.apiKey);
          } else {
            console.warn('[SocketService] Key response error or null from server:', data);
            resolve(null);
          }
        }
      };

      this.on('key_response', handler);
      this.emit('get_key', metadata || {});

      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.off('key_response', handler);
          console.warn('[SocketService] Timeout waiting for key_response from server.');
          resolve(null);
        }
      }, 5000);
    });
  }
}
